/**
 * Temporary frontend derivation of a rival's level/rank from total wins.
 *
 * Source of truth for the threshold table: `BATALLAS_DOCUMENTO_CLIENTE.md` /
 * `PROYECTO_BATALLAS_V1.0.md` (client-approved level system). Needed because
 * `local_tipddy_duel_list_opponents` always returns `level: 1` for every
 * user — `challenge_repository::list_enrolled_opponents()` builds opponent
 * objects without a level calculation (see ADR-0003,
 * docs/adr/0003-frontend-derives-rival-level-from-wins-temporarily.md).
 *
 * REMOVABLE: delete this module (and its single call site in
 * `BattleModal.jsx`) in the same change that ships a real backend `level`
 * field. It is intentionally the only place this domain rule is duplicated.
 *
 * Fed by `get_user_stats({token, userid, courseid}).wins` — NOT the `level`
 * field from `list_opponents`. Absent or malformed `wins` MUST degrade to no
 * rank line rather than showing an incorrect value — spec:
 * duel-opponent-rank, "wins field is absent or malformed".
 */

const LEVEL_TITLES = {
  1: "AGENTE",
  2: "INSPECTOR",
  3: "INSPECTOR JEFE",
  4: "COMISARIO",
  5: "COMISARIO GENERAL",
};

// Win-threshold table (upper bound inclusive per row), per the source doc:
// 0-5 -> 1, 6-15 -> 2, 16-30 -> 3, 31-50 -> 4, 51+ -> 5.
const THRESHOLDS = [
  { max: 5, level: 1 },
  { max: 15, level: 2 },
  { max: 30, level: 3 },
  { max: 50, level: 4 },
  { max: Infinity, level: 5 },
];

/**
 * Derives `{ level, title }` from a rival's total wins, or `null` if `wins`
 * is missing/non-numeric/negative.
 *
 * @param {*} wins
 * @returns {{level: number, title: string} | null}
 */
export function deriveLevelFromWins(wins) {
  if (typeof wins !== "number" || Number.isNaN(wins) || wins < 0) {
    return null;
  }

  const match = THRESHOLDS.find((threshold) => wins <= threshold.max);
  const level = match ? match.level : 5;

  return { level, title: LEVEL_TITLES[level] };
}

export default deriveLevelFromWins;
